// Apolo IDE — SpecReviewArtifact (S3 Epic E3b).
//
// Feed wrapper for the spec review artifact. Reads spec + turnToken from
// message.metadata and delegates rendering to SpecReviewPanel (presentational).
// Event dispatch (apolo:spec:accept / apolo:spec:skip) happens HERE so the
// panel stays unit-testable without window event knowledge.
//
// message shape expected:
//   { id, role:'tool', tool_name:'spec_review',
//     metadata: { spec: InlineSpec, turnToken: string, resolved?: 'accepted'|'skipped' } }
//
// If resolved, renders a compact "already handled" state and disables the panel.

import { CheckCircle, SkipForward } from 'lucide-react';
import SpecReviewPanel from '../MultiAgent/SpecReviewPanel.jsx';

function dispatchEvent(name, detail) {
  window.dispatchEvent(new CustomEvent(name, { detail }));
}

export default function SpecReviewArtifact({ message }) {
  const spec = message?.metadata?.spec;
  const turnToken = message?.metadata?.turnToken;
  const resolved = message?.metadata?.resolved;
  // C2 — the session this artifact belongs to. Threaded into the accept/skip
  // event detail so the resume listeners can bail on a cross-session mismatch.
  const sessionId = message?.metadata?.sessionId;

  // If there's no valid spec, render nothing (skip-safe).
  if (!spec || !turnToken) return null;

  if (resolved === 'accepted') {
    return (
      <div
        data-testid="spec-artifact-resolved-accepted"
        role="status"
        aria-live="polite"
        className="flex items-center gap-1.5 rounded-lg border border-apolo-borderSubtle bg-apolo-surface1/40 px-3 py-1.5 text-[11px] text-apolo-textTertiary"
      >
        <CheckCircle size={12} className="text-apolo-success" aria-hidden="true" />
        <span>Spec aceita — turno em andamento</span>
      </div>
    );
  }

  if (resolved === 'skipped') {
    return (
      <div
        data-testid="spec-artifact-resolved-skipped"
        role="status"
        aria-live="polite"
        className="flex items-center gap-1.5 rounded-lg border border-apolo-borderSubtle bg-apolo-surface1/40 px-3 py-1.5 text-[11px] text-apolo-textTertiary"
      >
        <SkipForward size={12} className="text-apolo-textTertiary" aria-hidden="true" />
        <span>Spec pulada — turno em andamento</span>
      </div>
    );
  }

  // I1/C2 — a turn that was superseded (re-submit) or abandoned (session switch).
  // The card is no longer actionable; show a quiet terminal state.
  if (resolved === 'superseded') {
    return (
      <div
        data-testid="spec-artifact-resolved-superseded"
        role="status"
        aria-live="polite"
        className="flex items-center gap-1.5 rounded-lg border border-apolo-borderSubtle bg-apolo-surface1/40 px-3 py-1.5 text-[11px] text-apolo-textTertiary"
      >
        <SkipForward size={12} className="text-apolo-textTertiary" aria-hidden="true" />
        <span>Spec descartada — substituída por outro pedido</span>
      </div>
    );
  }

  return (
    <div
      data-testid="spec-review-artifact"
      className="rounded-lg border border-apolo-borderSubtle bg-apolo-surface1/60 p-3"
    >
      <SpecReviewPanel
        spec={spec}
        onAccept={(acceptedSpec) =>
          dispatchEvent('apolo:spec:accept', { turnToken, sessionId, spec: acceptedSpec })
        }
        onSkip={() => dispatchEvent('apolo:spec:skip', { turnToken, sessionId })}
        onEdit={() => {}}
      />
    </div>
  );
}
